import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useOnboardingStore, { readToken } from "./useOnboardingStore";
import SubstepValue from "./substeps/SubstepValue";
import { buildInitialDraftValues, toValuesArray, patchStepValues } from "./flowValues";
import {
  isSubstepOptional,
  missingRequiredFields,
  getPrimitiveValueByKey,
} from "./formGate";
import OnboardingStatusScreen from "./OnboardingStatusScreen";
import { Loader } from "components/core/Common/Loader";
import ShowToast from "components/core/Common/Toast";
import { STATUS_BEGIN, STATUS_DONE } from "utils/constants/others";
import {
  FLOW_STATUS,
  SOR_STATUS,
  PAYMENT_STATUS,
  ONBOARDING_COPY,
  ONBOARDING_EVENT,
  DOCUMENT_VISIBILITY_STATE,
  SUBSTEP_TYPE,
  isDisclosureRadioItems,
} from "utils/constants/onboarding";
import { LS_KEYS } from "utils/constants/localStorageKeys";
import HPPaymentCheckout from "components/shared/HPPaymentCheckout";
import { PAYMENT_ORDER_TYPES } from "utils/constants/paymentOrderTypes";
import styles from "./Form.module.scss";

const REJECTED_EDIT_SESSION_KEY = "hp_rejected_onboarding_edit";
const REJECTED_CHANGES_SESSION_KEY = "hp_rejected_onboarding_changes";

// Returns true when one of the priority 1–6 status screens applies (not the ZPayKit or form case).
const getHasStatusScreen = (sorStatus, flowStatus, paymentStatus, rejectionReason, hasFee, retryPayment) => {
  if (sorStatus === SOR_STATUS.COMPLETED) return true;
  if (sorStatus === SOR_STATUS.IN_PROGRESS) return true;
  if (sorStatus === SOR_STATUS.INITIATED && rejectionReason && !retryPayment) return true;
  if (flowStatus !== FLOW_STATUS.COMPLETED) return false;
  if (paymentStatus === PAYMENT_STATUS.CANCELLED && !retryPayment) return true;
  if (paymentStatus === PAYMENT_STATUS.INITIATED) return true;
  const isPaymentDone = !hasFee || paymentStatus === PAYMENT_STATUS.COMPLETED;
  if (isPaymentDone && !retryPayment) return true;
  return false; // ZPayKit case — form renders with payment bar
};

function OnboardingForm() {
  const navigate = useNavigate();

  const flow                      = useOnboardingStore((s) => s.flow);
  const fetchFlow                 = useOnboardingStore((s) => s.fetchFlow);
  const reset                     = useOnboardingStore((s) => s.reset);
  const saveDraft                 = useOnboardingStore((s) => s.saveDraft);
  const markStepComplete          = useOnboardingStore((s) => s.markStepComplete);
  const completeFlow              = useOnboardingStore((s) => s.completeFlow);
  const onboardSelfOnboardingSeller = useOnboardingStore((s) => s.onboardSelfOnboardingSeller);
  const substepErrors             = useOnboardingStore((s) => s.substepErrors);
  const fetchFlowStatus           = useOnboardingStore((s) => s.fetchFlowStatus);
  const saveStatus                = useOnboardingStore((s) => s.saveStatus);
  const clearSubstepErrors        = useOnboardingStore((s) => s.clearSubstepErrors);
  const error                     = useOnboardingStore((s) => s.error);
  const clearError                = useOnboardingStore((s) => s.clearError);
  const onboardingFee             = useOnboardingStore((s) => s.onboardingFee);
  const onboardingPaymentGuid     = useOnboardingStore((s) => s.onboardingPaymentGuid);
  const clearOnboardingPaymentGuid = useOnboardingStore((s) => s.clearOnboardingPaymentGuid);
  const sorStatus                 = useOnboardingStore((s) => s.sorStatus);
  const paymentStatus             = useOnboardingStore((s) => s.paymentStatus);
  const rejectionReason           = useOnboardingStore((s) => s.rejectionReason);
  const sellerEmail               = useOnboardingStore((s) => s.sellerEmail);
  const sellerPhone               = useOnboardingStore((s) => s.sellerPhone);
  const selfOnboardStatus         = useOnboardingStore((s) => s.selfOnboardStatus);
  const pollPaymentExhausted      = useOnboardingStore((s) => s.pollPaymentExhausted);
  const startPaymentPoll          = useOnboardingStore((s) => s.startPaymentPoll);
  const startSorPoll              = useOnboardingStore((s) => s.startSorPoll);

  const [initialValues, setInitialValues] = useState({});
  const [editedValues, setEditedValues]   = useState({});
  const [activeStepIndex, setActiveStepIndex] = useState(null);
  const [retryPayment, setRetryPayment]   = useState(false);
  const [showFormView, setShowFormView]   = useState(false);
  const [rejectedChangeError, setRejectedChangeError] = useState(false);
  const hasFee = onboardingFee > 0;
  const flowStatus = flow?.flow_status;
  const hasSavedRejectedChanges = window.sessionStorage.getItem(REJECTED_CHANGES_SESSION_KEY) === "true";
  const isRejectedResubmissionPending = (
    sorStatus === SOR_STATUS.INITIATED &&
    flowStatus === FLOW_STATUS.COMPLETED &&
    hasSavedRejectedChanges
  );
  const visibleRejectionReason = isRejectedResubmissionPending ? "" : rejectionReason;

  // Authenticate guard — redirect to root if no token present.
  useEffect(() => {
    if (!readToken()) {
      navigate("/", { replace: true });
      return;
    }
    fetchFlow();

    // sushiweb Modal locks body scroll — release it on mount.
    document.body.classList.add("hp-onboarding-scroll-host");
    const clear = () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.overflow = "";
    };
    clear();
    const t1 = setTimeout(clear, 50);
    const t2 = setTimeout(clear, 250);
    const t3 = setTimeout(clear, 1000);
    return () => {
      document.body.classList.remove("hp-onboarding-scroll-host");
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  // Rebuild local draft state whenever the server flow changes (after fetchFlow).
  useEffect(() => {
    if (!flow) return;
    setInitialValues(buildInitialDraftValues(flow));
    setEditedValues({});
    const steps = flow?.flow_config?.steps ?? [];
    const firstPending = steps.findIndex((s) => !s.is_completed);
    setActiveStepIndex(firstPending >= 0 ? firstPending : steps.length > 0 ? steps.length - 1 : null);
  }, [flow]);

  // Poll A — start payment poll (5 s × 6 = 30 s) when payment becomes INITIATED.
  useEffect(() => {
    if (paymentStatus === PAYMENT_STATUS.INITIATED) startPaymentPoll();
  }, [paymentStatus]);

  // Poll SOR while flow is COMPLETED and SOR is INITIATED only when payment is not required
  // or payment is COMPLETED. Rejected requests also use SOR INITIATED, so skip when rejectionReason exists.
  useEffect(() => {
    if (fetchFlowStatus !== STATUS_DONE) return;
    const canSubmitWithoutPayment = !hasFee;
    const paymentCompleted = paymentStatus === PAYMENT_STATUS.COMPLETED;
    if (
      flowStatus === FLOW_STATUS.COMPLETED &&
      sorStatus === SOR_STATUS.INITIATED &&
      !visibleRejectionReason &&
      (canSubmitWithoutPayment || paymentCompleted)
    ) {
      startSorPoll();
    }
  }, [fetchFlowStatus, flowStatus, sorStatus, visibleRejectionReason, hasFee, paymentStatus]);

  // Auto-dismiss error toasts after 5 s.
  useEffect(() => {
    if (!error?.message) return undefined;
    const id = setTimeout(clearError, 5000);
    return () => clearTimeout(id);
  }, [error, clearError]);

  const draftValues = useMemo(
    () => ({ ...initialValues, ...editedValues }),
    [initialValues, editedValues]
  );

  const updateDraftValue = useCallback((substepId, value) => {
    setRejectedChangeError(false);
    setEditedValues((prev) => ({ ...prev, [substepId]: value }));
  }, []);

  const config  = flow?.flow_config ?? {};
  const steps   = config.steps ?? [];

  const valueByKey = useMemo(
    () => getPrimitiveValueByKey(steps, draftValues),
    [steps, draftValues]
  );

  const isRejectedApplication = sorStatus === SOR_STATUS.INITIATED && !!visibleRejectionReason;
  const isEditable = (
    flowStatus === FLOW_STATUS.CREATED ||
    flowStatus === FLOW_STATUS.ASSIGNED ||
    flowStatus === FLOW_STATUS.INITIATED
  );
  const isRejectedEditSession = (
    isRejectedApplication &&
    isEditable &&
    window.sessionStorage.getItem(REJECTED_EDIT_SESSION_KEY) === "true"
  );
  const isSaving        = saveStatus === STATUS_BEGIN;
  const firstPendingIdx = steps.findIndex((s) => !s.is_completed);
  const allComplete     = steps.length > 0 && firstPendingIdx === -1;

  // Contact substeps are skipped in generic SAVE_DRAFT. Verified values are sent
  // with MARK_STEP_COMPLETE; secondary contacts are persisted during OTP verification.
  const contactSubstepIds = useMemo(() => {
    const ids = new Set();
    for (const step of steps) {
      for (const sub of step?.substeps ?? []) {
        if (sub?.key === "contact_phone" || sub?.key === "contact_email") ids.add(sub.substep_id);
      }
    }
    return ids;
  }, [steps]);

  // Pure radio TODO_LIST items must be excluded from SAVE_DRAFT. The backend writes ALL
  // items in a list as a proto3 batch, defaulting unincluded bools to false. Excluding
  // them means proto3 never stores a false for these items; MARK_STEP_COMPLETE only sees
  // the selected item (patched to true via patchStepValues).
  const radioDisclosureSubstepIds = useMemo(() => {
    const ids = new Set();
    for (const step of steps) {
      for (const sub of step?.substeps ?? []) {
        if (sub.substep_type !== SUBSTEP_TYPE.TODO_LIST) continue;
        const items = sub.input_todo_list?.items ?? [];
        if (items.some((it) => (it.primitive_inputs ?? []).length > 0)) continue;
        for (const item of items) ids.add(item.substep_id);
      }
    }
    return ids;
  }, [steps]);

  const derivedTodoItemIds = useMemo(() => {
    const ids = new Set();
    for (const step of steps) {
      for (const sub of step?.substeps ?? []) {
        if (sub.substep_type !== SUBSTEP_TYPE.TODO_LIST) continue;
        const items = sub.input_todo_list?.items ?? [];
        if (!items.some((item) => (item.primitive_inputs ?? []).length > 0)) continue;
        if (isDisclosureRadioItems(items)) continue;
        for (const item of items) ids.add(item.substep_id);
      }
    }
    return ids;
  }, [steps]);

  useEffect(() => {
    if (fetchFlowStatus !== STATUS_DONE) return;
    if (isRejectedResubmissionPending) {
      window.sessionStorage.removeItem(REJECTED_EDIT_SESSION_KEY);
      return;
    }
    if (isRejectedApplication && isEditable) {
      if (window.sessionStorage.getItem(REJECTED_EDIT_SESSION_KEY) === "true") {
        setShowFormView(true);
      }
      return;
    }
    window.sessionStorage.removeItem(REJECTED_EDIT_SESSION_KEY);
    window.sessionStorage.removeItem(REJECTED_CHANGES_SESSION_KEY);
  }, [fetchFlowStatus, isRejectedApplication, isEditable, isRejectedResubmissionPending]);

  useEffect(() => {
    let refreshTimer;

    const refreshStatus = () => {
      if (
        document.visibilityState === DOCUMENT_VISIBILITY_STATE.HIDDEN ||
        showFormView ||
        isRejectedEditSession
      ) return;
      if (!getHasStatusScreen(
        sorStatus,
        flowStatus,
        paymentStatus,
        visibleRejectionReason,
        hasFee,
        retryPayment
      )) return;

      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(fetchFlow, 300);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === DOCUMENT_VISIBILITY_STATE.VISIBLE) refreshStatus();
    };

    window.addEventListener(ONBOARDING_EVENT.WINDOW_FOCUS, refreshStatus);
    window.addEventListener(ONBOARDING_EVENT.APP_RESUME, refreshStatus);
    document.addEventListener(ONBOARDING_EVENT.VISIBILITY_CHANGE, handleVisibilityChange);

    return () => {
      clearTimeout(refreshTimer);
      window.removeEventListener(ONBOARDING_EVENT.WINDOW_FOCUS, refreshStatus);
      window.removeEventListener(ONBOARDING_EVENT.APP_RESUME, refreshStatus);
      document.removeEventListener(ONBOARDING_EVENT.VISIBILITY_CHANGE, handleVisibilityChange);
    };
  }, [showFormView, isRejectedEditSession, sorStatus, flowStatus, paymentStatus, visibleRejectionReason, hasFee, retryPayment, fetchFlow]);

  const activeStepForGate = activeStepIndex != null ? steps[activeStepIndex] : null;

  const missingFields = useMemo(() => {
    if (!activeStepForGate) return [];
    return missingRequiredFields(activeStepForGate, draftValues, valueByKey);
  }, [activeStepForGate, draftValues, valueByKey]);

  const continueDisabled = missingFields.length > 0;

  useEffect(() => {
    if (missingFields.length > 0) {
      // eslint-disable-next-line no-console
      console.debug("[onboarding] Continue blocked, missing:", missingFields);
    }
  }, [missingFields]);

  // Sends all locally-changed substeps to the server in one SAVE_DRAFT batch.
  // Excludes: null values, verified contacts, radio disclosures (see radioDisclosureSubstepIds).
  const saveEditedSubsteps = useCallback(async () => {
    const changedValues = Object.entries(editedValues)
      .filter(([id, val]) => {
        if (val == null || val?.verified) return false;
        const numericId = Number(id);
        if (
          contactSubstepIds.has(numericId) ||
          radioDisclosureSubstepIds.has(numericId) ||
          derivedTodoItemIds.has(numericId)
        ) return false;
        const initial = initialValues[id];
        const hasText   = (val?.string_value ?? "").trim() !== "";
        const hasImages = Array.isArray(val?.string_list_value); // [] means "clear this field"
        const hasBool   = val?.bool_value === true;
        if (!hasText && !hasImages && !hasBool) return false;
        if (hasText) return val.string_value !== (initial?.string_value ?? "");
        if (hasImages) {
          return JSON.stringify(val.string_list_value) !== JSON.stringify(initial?.string_list_value ?? []);
        }
        return val.bool_value !== initial?.bool_value;
      })
      .map(([id, val]) => {
        const clean = { ...val };
        delete clean._previewUrls;
        delete clean.verified;
        return { substep_id: Number(id), ...clean };
      });
    if (changedValues.length === 0) {
      return {
        ok: true,
        hasRejectedChanges: window.sessionStorage.getItem(REJECTED_CHANGES_SESSION_KEY) === "true",
      };
    }
    const result = await saveDraft(changedValues);
    if (result?.ok) {
      if (isRejectedApplication) {
        window.sessionStorage.setItem(REJECTED_CHANGES_SESSION_KEY, "true");
      }
      const savedIds = changedValues.map((value) => String(value.substep_id));
      setInitialValues((prev) => {
        const next = { ...prev };
        for (const id of savedIds) next[id] = editedValues[id];
        return next;
      });
      setEditedValues((prev) => {
        const next = { ...prev };
        for (const id of savedIds) delete next[id];
        return next;
      });
    }
    return { ...result, hasRejectedChanges: result?.ok && isRejectedApplication };
  }, [editedValues, initialValues, saveDraft, contactSubstepIds, radioDisclosureSubstepIds, derivedTodoItemIds, isRejectedApplication]);

  const handleSaveDraft = async () => {
    clearSubstepErrors();
    await saveEditedSubsteps();
  };

  const handleCompleteStep = async (step) => {
    clearSubstepErrors();
    const saveRes = await saveEditedSubsteps();
    if (!saveRes?.ok) return;
    if (isRejectedApplication && step.is_completed) {
      setActiveStepIndex((current) => Math.min(current + 1, steps.length - 1));
      return;
    }
    await markStepComplete(step.step_id, patchStepValues(step, toValuesArray(initialValues, editedValues)));
  };

  const isLastStep = activeStepIndex === steps.length - 1;

  const handleLastStepSubmit = async () => {
    clearSubstepErrors();
    setRejectedChangeError(false);
    const saveRes = await saveEditedSubsteps();
    if (!saveRes?.ok) return;
    if (isRejectedApplication && !saveRes.hasRejectedChanges) {
      setRejectedChangeError(true);
      return;
    }
    if (!isRejectedApplication || !activeStep.is_completed) {
      const stepRes = await markStepComplete(
        activeStep.step_id,
        patchStepValues(activeStep, toValuesArray(initialValues, editedValues))
      );
      if (!stepRes?.ok) return;
    }
    const flowRes = await completeFlow();
    if (!flowRes?.ok) return;
    window.sessionStorage.removeItem(REJECTED_EDIT_SESSION_KEY);
    if (!hasFee) {
      // Await so the UI shows "Finalizing…" (selfOnboardStatus=STATUS_BEGIN) before
      // transitioning to the "Submitted" screen (selfOnboardStatus=STATUS_DONE).
      const selfRes = await onboardSelfOnboardingSeller();
      if (selfRes?.ok) startSorPoll();
      // On failure: selfOnboardStatus=STATUS_FAIL → retry button appears on submitted screen.
      setShowFormView(false);
    } else if (isRejectedApplication) {
      setRetryPayment(true);
    }
    // Paid submissions continue through checkout after the flow is completed.
  };

  const handleRetryOnboard = useCallback(async () => {
    const selfRes = await onboardSelfOnboardingSeller();
    if (selfRes?.ok) startSorPoll();
  }, [onboardSelfOnboardingSeller, startSorPoll]);

  const handleEditRejected = useCallback(() => {
    window.sessionStorage.setItem(REJECTED_EDIT_SESSION_KEY, "true");
    window.sessionStorage.removeItem(REJECTED_CHANGES_SESSION_KEY);
    clearOnboardingPaymentGuid();
    setRejectedChangeError(false);
    setActiveStepIndex(0);
    setShowFormView(true);
  }, [clearOnboardingPaymentGuid]);

  const handleSellerHubLogin = useCallback(() => {
    reset();
    const isApp = window.localStorage.getItem(LS_KEYS.REFERRER_PLATFORM) === "app";
    if (isApp) {
      const loginChannel = window.sellerHubLoginHandler || window.logoutChannelMessageHandler;
      if (loginChannel) {
        loginChannel.postMessage("login");
        return;
      }
    }
    window.location.href = "/";
  }, [reset]);

  // Called by the ZPayKit SDK at checkout. Returns the payment guid. Reuses the stored
  // guid on retry — re-calling onboardSelfOnboardingSeller would error "already initiated".
  const fetchOnboardingPaymentGuid = useCallback(async () => {
    if (onboardingPaymentGuid > 0) return onboardingPaymentGuid;
    const res = await onboardSelfOnboardingSeller();
    if (!res?.ok || !(res.paymentGuid > 0)) {
      throw new Error(res?.message || "Failed to initiate payment. Please try again.");
    }
    return res.paymentGuid;
  }, [onboardingPaymentGuid, onboardSelfOnboardingSeller]);

  const goToStep = (idx) => {
    setActiveStepIndex(idx);
  };

  const handlePrevious = () => {
    if (activeStepIndex == null || activeStepIndex <= 0) return;
    goToStep(activeStepIndex - 1);
  };

  const stepperItems = steps.map((serverStep, idx) => ({
    idx,
    name: serverStep.header_text || `Step ${idx + 1}`,
    isDone: !!serverStep.is_completed,
    serverStep,
  }));

  const firstPendingShellIdx = stepperItems.findIndex((s) => !s.isDone);
  const stepperActiveIdx = activeStepIndex != null ? activeStepIndex : firstPendingShellIdx;

  // ── Loading states ────────────────────────────────────────────────────────────

  if (fetchFlowStatus === STATUS_BEGIN && !flow) {
    return (
      <div className={styles.page}>
        <Header />
        <div className={styles.centered}><Loader /></div>
      </div>
    );
  }

  if (!flow) {
    return (
      <div className={styles.page}>
        <Header />
        <div className={styles.centered}>
          {ONBOARDING_COPY.loadErrorTitle}{" "}
          <button className={styles.backLink} onClick={() => fetchFlow()}>
            {ONBOARDING_COPY.retry}
          </button>
        </div>
      </div>
    );
  }

  // ── Status / success screens ──────────────────────────────────────────────────
  // Priority-ordered: fully onboarded → under review → rejected → payment states → submitted.
  // hasStatusScreen is false for Priority 7 (ZPayKit) and 8 (normal form) so those fall through.
  const hasStatusScreen = !showFormView && !isRejectedEditSession && getHasStatusScreen(sorStatus, flowStatus, paymentStatus, visibleRejectionReason, hasFee, retryPayment);

  if (hasStatusScreen) {
    return (
      <div className={styles.page}>
        <Header />
        <OnboardingStatusScreen
          sorStatus={sorStatus}
          flowStatus={flowStatus}
          paymentStatus={paymentStatus}
          rejectionReason={visibleRejectionReason}
          sellerPhone={sellerPhone}
          sellerEmail={sellerEmail}
          hasFee={hasFee}
          onboardingFee={onboardingFee}
          retryPayment={retryPayment}
          setRetryPayment={setRetryPayment}
          selfOnboardStatus={selfOnboardStatus}
          pollPaymentExhausted={pollPaymentExhausted}
          onViewForm={() => setShowFormView(true)}
          onFetchFlow={fetchFlow}
          onRetryOnboard={handleRetryOnboard}
          onEditRejected={handleEditRejected}
          onLoginSellerHub={handleSellerHubLogin}
        />
      </div>
    );
  }

  // ── Form render ───────────────────────────────────────────────────────────────

  const activeStep = activeStepIndex != null ? steps[activeStepIndex] : null;

  return (
    <div className={styles.page}>
      <Header />

      {error?.message && (
        <ShowToast key={error.message} errorMsg={error.message} type="error" />
      )}

      {isRejectedApplication && isEditable && (
        <div className={`${styles.container} ${styles.rejectionEditContainer}`}>
          <div className={styles.rejectionEditBanner}>
            <strong>Update and resubmit your application</strong>
            <span>{visibleRejectionReason}</span>
          </div>
        </div>
      )}

      <Stepper
        items={stepperItems}
        activeIndex={stepperActiveIdx}
        onClickStep={(idx) => {
          const item = stepperItems[idx];
          if (!item) return;
          const locked = !item.isDone && idx !== firstPendingShellIdx;
          if (!locked) goToStep(idx);
        }}
      />

      {flowStatus === FLOW_STATUS.COMPLETED && hasFee && (!isRejectedApplication || retryPayment) && (paymentStatus === PAYMENT_STATUS.UNSPECIFIED || retryPayment) && (
        <div className={styles.paymentBar}>
          <HPPaymentCheckout
            domId="onboarding-payment"
            amount={onboardingFee}
            orderType={PAYMENT_ORDER_TYPES.SELLER_ONBOARDING}
            fetchGuid={fetchOnboardingPaymentGuid}
            onClose={() => { setRetryPayment(false); fetchFlow(); }}
            redirectUrl={`${window.location.origin}/onboarding/form`}
          />
        </div>
      )}

      <div className={styles.container}>
        {activeStep ? (
          <StepDetail
            step={activeStep}
            stepNumber={activeStepIndex + 1}
            draftValues={draftValues}
            valueByKey={valueByKey}
            substepErrors={substepErrors}
            onChange={updateDraftValue}
            isReadOnly={!isEditable}
          />
        ) : (
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>{config.flow_name}</h2>
            <p className={styles.cardSubtitle}>
              {allComplete
                ? ONBOARDING_COPY.overviewSubtitleAllDone
                : ONBOARDING_COPY.overviewSubtitlePending}
            </p>
            {!allComplete && firstPendingIdx >= 0 && (
              <button
                className={styles.primaryBtn}
                style={{ flex: "0 0 auto" }}
                onClick={() => setActiveStepIndex(firstPendingIdx)}
              >
                {ONBOARDING_COPY.continue} with Step {firstPendingIdx + 1}
              </button>
            )}
          </div>
        )}
      </div>

      {isLastStep && hasFee && isEditable && (
        <div className={styles.container}>
          <ApplicationFeeCard fee={onboardingFee} />
        </div>
      )}

      {showFormView && !isEditable && (
        <div className={styles.bottomBar}>
          <div className={styles.bottomBarInner}>
            <button className={styles.primaryBtn} onClick={() => setShowFormView(false)}>
              View Application Status
            </button>
          </div>
        </div>
      )}

      {isEditable && (
        <div className={styles.bottomBar}>
          <div className={styles.bottomBarInner}>
            {activeStep && (
              <>
                <div className={styles.savedHint}>
                  {rejectedChangeError
                    ? "Update at least one field before resubmitting"
                    : isSaving
                    ? ONBOARDING_COPY.saving
                    : saveStatus === STATUS_DONE
                      ? ONBOARDING_COPY.saved
                      : ""}
                </div>
                {activeStepIndex > 0 && (
                  <button
                    type="button"
                    className={styles.secondaryBtn}
                    onClick={handlePrevious}
                    disabled={isSaving}
                  >
                    {ONBOARDING_COPY.previous}
                  </button>
                )}
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={handleSaveDraft}
                  disabled={isSaving}
                >
                  {isSaving ? ONBOARDING_COPY.saving : ONBOARDING_COPY.saveDraft}
                </button>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  onClick={() => isLastStep ? handleLastStepSubmit() : handleCompleteStep(activeStep)}
                  disabled={isSaving || continueDisabled || (isLastStep && isRejectedApplication && !hasSavedRejectedChanges)}
                  title={
                    continueDisabled
                      ? `Fill in: ${missingFields.join(", ")}`
                      : isLastStep && isRejectedApplication && !hasSavedRejectedChanges
                        ? "Update and save at least one field before resubmitting"
                        : undefined
                  }
                >
                  {isSaving
                    ? ONBOARDING_COPY.pleaseWait
                    : isLastStep && isRejectedApplication
                      ? hasFee
                        ? "Resubmit & Pay Refundable Fee"
                        : "Resubmit application"
                      : isLastStep && hasFee
                      ? "Submit & Pay Refundable Fee"
                      : isLastStep
                        ? ONBOARDING_COPY.saveAndSubmit
                        : ONBOARDING_COPY.continue}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Header() {
  const reset = useOnboardingStore((s) => s.reset);

  const handleLogout = () => {
    reset();
    const isApp = window.localStorage.getItem(LS_KEYS.REFERRER_PLATFORM) === "app";
    if (isApp && window.logoutChannelMessageHandler) {
      window.logoutChannelMessageHandler.postMessage("exit");
    } else {
      window.location.href = "/";
    }
  };

  return (
    <header className={styles.header}>
      <img
        className={styles.headerLogo}
        src="/logos/hp_seller_logo.png"
        alt="Hyperpure Seller"
      />
      {readToken() && (
        <button type="button" className={styles.exitBtn} onClick={handleLogout}>
          <img src="/logos/logout_logo.svg" alt="" className={styles.exitIcon} />
          Exit
        </button>
      )}
    </header>
  );
}

function Stepper({ items, activeIndex, onClickStep }) {
  if (!items.length) return null;
  return (
    <div className={styles.stepper}>
      {items.map((item, idx) => {
        const isActive  = idx === activeIndex;
        const dotClass  = `${styles.stepDot} ${item.isDone ? styles.stepDotDone : isActive ? styles.stepDotActive : ""}`;
        const nameClass = `${styles.stepName} ${item.isDone ? styles.stepNameDone : isActive ? styles.stepNameActive : ""}`;
        const lineClass = `${styles.stepLine} ${item.isDone ? styles.stepLineDone : ""}`;
        return (
          <React.Fragment key={idx}>
            <div
              className={`${styles.stepGroup} ${styles.clickable}`}
              onClick={() => onClickStep(idx)}
              role="button"
              tabIndex={0}
            >
              <div className={dotClass}>{!item.isDone && idx + 1}</div>
              <div className={nameClass}>{item.name}</div>
            </div>
            {idx < items.length - 1 && <div className={lineClass} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StepDetail({ step, stepNumber, draftValues, valueByKey, substepErrors, onChange, isReadOnly }) {
  const substeps = step?.substeps ?? [];

  const substepKeyToId = React.useMemo(() => {
    const map = {};
    substeps.forEach((s) => { if (s.key) map[s.key] = s.substep_id; });
    return map;
  }, [substeps]);

  return (
    <div className={styles.card}>
      <h2 className={styles.cardTitle}>{step.header_text || `Step ${stepNumber}`}</h2>
      {step.instruction_title_text && (
        <p className={styles.cardSubtitle}>{step.instruction_title_text}</p>
      )}

      {substeps.map((sub) => {
        const isOptional = isSubstepOptional(sub, valueByKey);
        const todoItems  = sub.input_todo_list?.items ?? [];
        const isFormGroupTodo =
          sub.substep_type === SUBSTEP_TYPE.TODO_LIST &&
          todoItems.some((it) => (it.primitive_inputs ?? []).length > 0) &&
          !isDisclosureRadioItems(todoItems);
        return (
          <div key={sub.substep_id} className={styles.substep}>
            {sub.title && (
              <div className={styles.substepTitle}>
                {sub.title}
                {!isOptional && !isFormGroupTodo && <span className={styles.requiredMark}>*</span>}
              </div>
            )}
            {sub.subtitle && <div className={styles.substepSubtitle}>{sub.subtitle}</div>}
            <SubstepValue
              substep={sub}
              value={draftValues[sub.substep_id]}
              onChange={onChange}
              error={substepErrors[sub.substep_id]}
              allValues={draftValues}
              allErrors={substepErrors}
              valueByKey={valueByKey}
              isReadOnly={isReadOnly}
              substepKeyToId={substepKeyToId}
            />
          </div>
        );
      })}
    </div>
  );
}

function ApplicationFeeCard({ fee }) {
  const fmt = (n) =>
    `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className={styles.feeCard}>
      <div className={styles.refundableBadge}>
        <span className={styles.refundableTick}>✓</span>
        <div>
          <div className={styles.refundableTitle}>100% REFUNDABLE</div>
          <div className={styles.refundableDesc}>
            This fee is fully refunded after every application review, whether your application is approved or needs changes.
          </div>
        </div>
      </div>
      <div className={styles.feeBreakdown}>
        <div className={styles.feeRow}>
          <span>Application Fee</span>
          <span>{fmt(fee)}</span>
        </div>
      </div>
    </div>
  );
}

export default OnboardingForm;
